import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ApiClient from "../lib/api";
import { TID } from "../lib/testIds";
import { Section } from "../components/Atoms";

const CURRENCIES = ["GBP", "PHP", "USD", "EUR", "INR"];

export default function DemoLauncher({ config }) {
  const nav = useNavigate();

  const [token, setToken] = useState("");
  const [playerId, setPlayerId] = useState("player_demo_001");
  const [opId, setOpId] = useState("op_lunaplay");
  const [currency, setCurrency] = useState("GBP");
  const [balance, setBalance] = useState(10000);
  const [busy, setBusy] = useState(false);

  const mode = config?.mode;

  useEffect(() => {
    if (config?.operator_id) setOpId(config.operator_id);
    if (config?.default_currency) setCurrency(config.default_currency);

    if (mode === "mock" && !token) {
      setToken("demo-token-" + Math.random().toString(36).slice(2, 8));
    }
  }, [config, mode, token]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);

    try {
      let launchToken = token;

      if (mode === "mock") {
        if (!launchToken) {
          launchToken = "demo-token-" + Math.random().toString(36).slice(2, 8);
          setToken(launchToken);
        }

        await ApiClient.demoLaunch({
          launch_token: launchToken,
          player_id: playerId,
          operator_id: opId,
          currency,
          balance: Number(balance),
        });

        toast.success("Mock session seeded");
      } else {
        const launched = await ApiClient.gameLaunch({
          player_id: playerId,
          currency,
          jurisdiction: "GI",
        });

        launchToken = launched.launch_token;

        if (!launchToken) {
          throw new Error("CoreBridge did not return a launch token");
        }

        setToken(launchToken);
        toast.success("Live CoreBridge session created");
      }

      nav(`/?launch_token=${encodeURIComponent(launchToken)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 fade-in">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--kk-gold-soft)] mb-4">
          CoreBridge Provider · Demo Launcher
        </div>

        <h1 className="font-display text-5xl sm:text-6xl text-[var(--kk-ivory)] leading-[1.04]">
          Kara <span className="text-[var(--kk-gold)]">·</span> Krus
        </h1>

        <p className="text-[var(--kk-muted)] mt-4 max-w-md leading-relaxed">
          A premium coin-flip game running as an external provider on top of the live CoreBridge operator API.
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
          <Tile label="Provider" value={config?.provider_id || "—"} />
          <Tile label="Game" value={config?.game_id || "—"} />
          <Tile label="Operator" value={config?.operator_name || "—"} />
          <Tile label="Currency" value={config?.default_currency || "—"} />
          <Tile label="Payout" value={`${config?.payout_multiplier || "—"}x`} gold />
          <Tile label="Mode" value={(mode || "—").toUpperCase()} gold />
        </div>
      </div>

      <Section title="Launch Game">
        <form className="space-y-4" onSubmit={submit} data-testid={TID.launcherForm}>
          <Field label="Launch Token">
            <input
              data-testid={TID.launcherToken}
              className="kk-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={mode === "live" ? "Generated by CoreBridge on launch" : "Mock token"}
              readOnly={mode === "live"}
            />
          </Field>

          <Field label="Player ID">
            <input
              data-testid={TID.launcherPlayerId}
              className="kk-input"
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Operator ID">
              <input
                data-testid={TID.launcherOperatorId}
                className="kk-input"
                value={opId}
                onChange={(e) => setOpId(e.target.value)}
                readOnly={mode === "live"}
              />
            </Field>

            <Field label="Currency">
              <select
                data-testid={TID.launcherCurrency}
                className="kk-input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {mode === "mock" && (
            <Field label="Seeded Balance">
              <input
                data-testid={TID.launcherBalance}
                className="kk-input"
                type="number"
                min="0"
                step="100"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
              />
            </Field>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid={TID.launcherSubmit}
            className="btn-gold w-full py-3 text-[12px]"
          >
            {busy ? "Launching…" : "Launch Kara Krus"}
          </button>

          <p className="text-[10.5px] text-[var(--kk-muted)] leading-relaxed">
            In <strong className="text-[var(--kk-ivory)]">{mode}</strong> mode, this{" "}
            {mode === "mock" ? "seeds a local mock session" : "creates a real CoreBridge launch session first"}.
          </p>
        </form>
      </Section>

      <style>{`
        .kk-input {
          width: 100%;
          background: var(--kk-graphite-2);
          border: 1px solid var(--kk-line);
          color: var(--kk-ivory);
          padding: 10px 12px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12.5px;
          border-radius: 3px;
          outline: none;
          transition: border-color 150ms ease;
        }
        .kk-input:focus { border-color: var(--kk-gold); }
        .kk-input[readonly] { opacity: 0.8; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-[0.22em] text-[var(--kk-muted)] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Tile({ label, value, gold }) {
  return (
    <div className={`panel p-4 ${gold ? "panel-gold" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--kk-muted)] mb-2">
        {label}
      </div>
      <div className={`font-mono text-[14px] ${gold ? "text-[var(--kk-gold-soft)]" : "text-[var(--kk-ivory)]"}`}>
        {value}
      </div>
    </div>
  );
}
